import { formatMoneyIn, toLocale } from '@zuruflex/i18n';
import { REQUIRED_VEHICLE_IMAGE_KINDS, type Vehicle } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Money,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, LayoutGrid, Plus, Rows3, Search, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { AddCarDialog } from '../components/AddCarDialog.js';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';

/** What a car still needs photographed before it can be sent for review. */
function missingPhotoKinds(car: Vehicle): number {
  const have = new Set(car.images.map((image) => image.kind));
  return REQUIRED_VEHICLE_IMAGE_KINDS.filter((kind) => !have.has(kind)).length;
}

const LISTING_VARIANT = {
  PUBLISHED: 'success',
  IN_REVIEW: 'warning',
  REJECTED: 'danger',
  DRAFT: 'default',
  UNLISTED: 'outline',
} as const;

const FILTERS = ['all', 'live', 'notLive', 'needsYou'] as const;
type Filter = (typeof FILTERS)[number];

const VIEWS = ['grid', 'list'] as const;
type View = (typeof VIEWS)[number];

const SORTS = ['added', 'name', 'rateDesc', 'rateAsc', 'rated'] as const;
type Sort = (typeof SORTS)[number];

function isFilter(value: string | null): value is Filter {
  return value !== null && (FILTERS as readonly string[]).includes(value);
}

function isView(value: string | null): value is View {
  return value !== null && (VIEWS as readonly string[]).includes(value);
}

function isSort(value: string | null): value is Sort {
  return value !== null && (SORTS as readonly string[]).includes(value);
}

/**
 * Plates are written a dozen ways — RAG 125 C, rag-125-c, RAG125C — and a
 * partner hunting for one types whichever is in their head. Both sides of
 * the comparison lose everything that is not a letter or a digit.
 */
function squash(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Make, model, year and plate: the four things a car is called. */
function hit(car: Vehicle, query: string): boolean {
  const loose = squash(query);
  if (!loose) return true;
  return squash(`${car.year} ${car.make} ${car.model} ${car.plateNumber}`).includes(loose);
}

function order(sort: Sort): (a: Vehicle, b: Vehicle) => number {
  switch (sort) {
    case 'name':
      return (a, b) =>
        `${a.make} ${a.model}`.localeCompare(`${b.make} ${b.model}`) || a.year - b.year;
    case 'rateDesc':
      return (a, b) => b.dailyRate - a.dailyRate;
    case 'rateAsc':
      return (a, b) => a.dailyRate - b.dailyRate;
    case 'rated':
      // A car nobody has reviewed is not a nought-star car, so it sorts
      // below the rated ones rather than under the worst of them.
      return (a, b) => (b.rating?.average ?? -1) - (a.rating?.average ?? -1);
    default:
      return (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  }
}

/**
 * A car is waiting on its owner when Zuruflex has turned it down, or when it is
 * still a draft that cannot be sent — a draft with every photograph in is
 * waiting on nobody but the person who has not pressed the button, which
 * the Draft badge already says.
 */
function needsYou(car: Vehicle): boolean {
  return car.listingStatus === 'REJECTED' || missingPhotoKinds(car) > 0;
}

function matches(car: Vehicle, filter: Filter): boolean {
  switch (filter) {
    case 'live':
      return car.listingStatus === 'PUBLISHED';
    case 'notLive':
      return car.listingStatus !== 'PUBLISHED';
    case 'needsYou':
      return needsYou(car);
    default:
      return true;
  }
}

export function FleetPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const { partner } = usePartner();

  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /*
   * Which cars and how they are drawn both live in the URL. A partner who
   * keeps the portal open on the cars that still need photographs should
   * get those back on a reload, and "look at this one" is a link.
   */
  const [params, setParams] = useSearchParams();
  const filter: Filter = isFilter(params.get('show')) ? (params.get('show') as Filter) : 'all';
  const view: View = isView(params.get('view')) ? (params.get('view') as View) : 'grid';
  const sort: Sort = isSort(params.get('sort')) ? (params.get('sort') as Sort) : 'added';
  const query = params.get('q') ?? '';

  const set = (key: 'show' | 'view' | 'sort' | 'q', value: string) => {
    const next = new URLSearchParams(params);
    // An empty search is no search, not a search for nothing.
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const {
    data,
    isPending,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ['my-fleet'],
    queryFn: () => api.listVehicles({ partnerId: partner.id, perPage: 100 }),
  });

  const cars = data?.items ?? [];
  /*
   * Search first, so the tab counts read against what was searched for —
   * "Live 2" beside a search for "Vitz" means two live Vitzes, which is
   * the question that was asked.
   */
  const found = cars.filter((car) => hit(car, query));
  const shown = found.filter((car) => matches(car, filter)).sort(order(sort));

  const rate = (car: Vehicle) => (
    <Money
      amount={car.dailyRate}
      currency={car.currency}
      suffix={<span className="text-xs text-text-muted"> {t('common:unit.perDay')}</span>}
    />
  );

  /** Who drives it, and what the owner is paid for supplying one. */
  const driverLine = (car: Vehicle) =>
    car.driverPolicy === 'SELF_DRIVE_ONLY' ? null : (
      <>
        {t(`common:driverPolicy.${car.driverPolicy}`)}
        {car.driverDailyRate
          ? ` · ${t('fleet.driverRate', {
              amount: formatMoneyIn(car.driverDailyRate, car.currency, locale),
            })}`
          : ''}
      </>
    );

  return (
    <>
      <PageHeader
        title={t('fleet.title')}
        description={
          !data
            ? t('common:state.loading')
            : shown.length === cars.length
              ? t('fleet.count', { count: data.total })
              : /* "24 cars" over a list of three is a page arguing with
                   itself, so the header counts what is actually below it. */
                t('fleet.showing', { shown: shown.length, total: cars.length })
        }
        action={
          <Button disabled={partner.status === 'SUSPENDED'} onClick={() => setAdding(true)}>
            <Plus className="size-4" />
            {t('fleet.addCar')}
          </Button>
        }
      />

      {notice ? (
        <Alert variant="success" className="mb-6">
          {notice}
        </Alert>
      ) : null}

      {/*
        The filter and the layout are two different questions — which cars,
        and how much of each one — so they are two controls rather than one
        list of six things.
      */}
      {!isPending && cars.length > 0 ? (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <Tabs value={filter} onValueChange={(next) => set('show', next)}>
            <TabsList className="flex-wrap">
              {FILTERS.map((key) => {
                const count = found.filter((car) => matches(car, key)).length;
                return (
                  <TabsTrigger key={key} value={key} className="gap-2">
                    {t(`fleet.filter.${key}`)}
                    <span
                      className={cn(
                        'text-xs tabular-nums',
                        key === 'needsYou' && count > 0 ? 'font-bold text-warning' : 'opacity-60',
                      )}
                    >
                      {count}
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
            <div className="relative min-w-48 flex-1 sm:max-w-64">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted"
                aria-hidden
              />
              <Input
                type="search"
                value={query}
                aria-label={t('fleet.search')}
                placeholder={t('fleet.search')}
                onChange={(event) => set('q', event.target.value)}
                /* WebKit draws its own clear affordance on a search input,
                   which sat next to ours as a second, differently-styled
                   cross. Ours stays, because it is the one that matches. */
                className="pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
              />
              {query ? (
                <button
                  type="button"
                  aria-label={t('common:action.close')}
                  onClick={() => set('q', '')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-text-muted hover:bg-surface-hover hover:text-text"
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
            </div>

            <Select value={sort} onValueChange={(next) => set('sort', next)}>
              <SelectTrigger
                className="w-auto min-w-40 shrink-0"
                aria-label={t('fleet.sort.label')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`fleet.sort.${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <TooltipProvider>
              <div
                role="group"
                aria-label={t('fleet.view.label')}
                className="flex shrink-0 items-center gap-1 rounded-pill bg-surface-sunken p-1"
              >
                {VIEWS.map((key) => {
                  const Icon = key === 'grid' ? LayoutGrid : Rows3;
                  return (
                    <Tooltip key={key}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-pressed={view === key}
                          aria-label={t(`fleet.view.${key}`)}
                          onClick={() => set('view', key)}
                          className={cn(
                            'inline-flex size-8 items-center justify-center rounded-pill transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                            view === key
                              ? 'bg-surface-active text-text shadow-soft'
                              : 'text-text-muted hover:text-text',
                          )}
                        >
                          <Icon className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t(`fleet.view.${key}`)}</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </TooltipProvider>
          </div>
        </div>
      ) : null}

      {loadError ? (
        <ErrorState error={loadError} onRetry={() => void refetch()} />
      ) : isPending ? (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-hidden>
          {[0, 1, 2, 3, 4, 5].map((card) => (
            <li key={card}>
              <Skeleton className="h-72 rounded-card" />
            </li>
          ))}
        </ul>
      ) : cars.length === 0 ? (
        <EmptyState
          title={t('fleet.none')}
          description={t('fleet.noneBody')}
          action={
            <Button disabled={partner.status === 'SUSPENDED'} onClick={() => setAdding(true)}>
              <Plus className="size-4" />
              {t('fleet.addCar')}
            </Button>
          }
        />
      ) : shown.length === 0 ? (
        /* The fleet is not empty — this corner of it is. Saying "no cars
           yet" here would be a lie with an Add button attached, and a
           partner who has just typed a plate needs to know it was the
           typing that emptied the page and not the tab. */
        <EmptyState
          title={query ? t('fleet.empty.search', { query }) : t(`fleet.empty.${filter}`)}
          action={
            <Button
              variant="outline"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete('q');
                next.set('show', 'all');
                setParams(next, { replace: true });
              }}
            >
              {t('fleet.clearFilters')}
            </Button>
          }
        />
      ) : view === 'list' ? (
        /*
         * The same cars at a tenth of the height. Twenty-four of them as
         * cards is eight screens of scrolling to answer "is the Vitz up
         * yet"; as rows it is one.
         */
        <ul className="overflow-hidden rounded-card border border-border-default bg-surface-raised">
          {shown.map((car) => (
            <li key={car.id} className="border-b border-border-default last:border-b-0">
              <Link
                to={`/fleet/${car.id}`}
                className="group flex items-center gap-4 px-3 py-2.5 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
              >
                {car.images[0] ? (
                  <img
                    src={car.images[0].url}
                    alt=""
                    className="size-12 shrink-0 rounded-field object-cover"
                  />
                ) : (
                  <div className="size-12 shrink-0 rounded-field bg-surface-sunken" />
                )}

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {car.year} {car.make} {car.model}
                  </p>
                  <p className="truncate text-xs text-text-muted">
                    <span className="font-mono">{car.plateNumber}</span>
                    {driverLine(car) ? <> · {driverLine(car)}</> : null}
                  </p>
                </div>

                {/* What is wrong, where the eye already is — a list is read
                    down the right-hand edge, not across each row. */}
                <div className="hidden min-w-0 shrink-0 text-right text-xs sm:block">
                  {car.listingStatus === 'REJECTED' && car.reviewNote ? (
                    <span className="line-clamp-1 text-danger">{car.reviewNote}</span>
                  ) : missingPhotoKinds(car) > 0 ? (
                    <span className="text-text-muted">
                      {t('fleet.needsKinds', { count: missingPhotoKinds(car) })}
                    </span>
                  ) : null}
                </div>

                <div className="hidden w-32 shrink-0 text-right md:block">{rate(car)}</div>

                <Badge
                  variant={LISTING_VARIANT[car.listingStatus]}
                  className="hidden shrink-0 sm:inline-flex"
                >
                  {t(`common:listingStatus.${car.listingStatus}`)}
                </Badge>

                <ChevronRight
                  className="size-4 shrink-0 text-text-muted group-hover:text-text"
                  aria-hidden
                />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        /*
         * Each card is a way into the car rather than a control panel for
         * it. Photographs, dates, the rate and the paperwork all used to
         * hang off this tile as separate popups, which made a grid of
         * twenty-four cars a grid of ninety-six buttons.
         */
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((car) => (
            <li key={car.id}>
              <Link
                to={`/fleet/${car.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-card border border-border-default bg-surface-raised transition-shadow hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {car.images[0] ? (
                  <img
                    src={car.images[0].url}
                    alt={car.images[0].alt ?? ''}
                    className="aspect-video w-full object-cover"
                  />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center bg-surface-sunken text-sm text-text-muted">
                    {t('fleet.needsPhotos')}
                  </div>
                )}

                <div className="flex flex-1 flex-col gap-3 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-text">
                        {car.year} {car.make} {car.model}
                      </p>
                      <p className="font-mono text-xs text-text-muted">{car.plateNumber}</p>
                    </div>
                    <Badge variant={LISTING_VARIANT[car.listingStatus]}>
                      {t(`common:listingStatus.${car.listingStatus}`)}
                    </Badge>
                  </div>

                  {rate(car)}

                  {/* An owner running a mixed fleet needs to see at a glance
                      which cars go out with a driver — and their own rate for
                      one, not the all-in figure a customer is shown. */}
                  {driverLine(car) ? (
                    <p className="text-xs text-text-muted">{driverLine(car)}</p>
                  ) : null}

                  {/* The listing gate wants a set of photographs, not just
                      one, so the shortfall is named on the card rather than
                      waiting to be a rejection later. */}
                  {missingPhotoKinds(car) > 0 ? (
                    <p className="text-xs text-text-muted">
                      {t('fleet.needsKinds', { count: missingPhotoKinds(car) })}
                    </p>
                  ) : null}

                  {car.listingStatus === 'REJECTED' && car.reviewNote ? (
                    <p className="line-clamp-2 text-xs text-danger">{car.reviewNote}</p>
                  ) : null}

                  <span className="mt-auto inline-flex items-center gap-1 pt-1 text-sm font-medium text-text-muted group-hover:text-text">
                    {t('fleet.manage')}
                    <ChevronRight className="size-4" />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <AddCarDialog
          onClose={(created) => {
            setAdding(false);
            if (created) setNotice(t('addCar.created'));
          }}
        />
      ) : null}
    </>
  );
}
